import * as React from 'react';

import { facilityURL } from '@admin/config/routes';
import { usePermissionKey } from '@admin/hooks/use_permission';
import { client } from '@admin/libraries/apollo';
import { useModifyRegionMutation, type RegionQuery } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { CardToolbarLead } from '@admin/styled/card_toolbar_lead';
import { EmptyState } from '@admin/styled/empty_state';
import { MutedText } from '@admin/styled/muted_text';
import { Pill } from '@admin/styled/pill';
import { SectionActions } from '@admin/styled/section_actions';
import { SectionMeta } from '@admin/styled/section_meta';
import { SectionTitle } from '@admin/styled/section_title';
import { Table } from '@admin/styled/table';
import { TableWrap } from '@admin/styled/table_wrap';

type RegionFacility = NonNullable<RegionQuery['region']>['facilities'][number];

type RegionFacilitiesProps = {
  regionID: string;
  defaultWarehouse?: { id: string; name: string } | null;
  defaultDepot?: { id: string; name: string } | null;
  facilities: RegionFacility[];
  modifyingRegion: boolean;
  onUpdated(): void;
};

function serviceNames(facility: RegionFacility): string {
  return facility.serviceTypes.map((service) => service.name).join(', ');
}

function regionNames(facility: RegionFacility): string {
  return facility.regions.map((region) => region.name).join(', ');
}

function RegionDefaultCell({
  linked,
  isRegionDefault,
  canSet,
  setting,
  onMakeDefault,
}: {
  linked: boolean;
  isRegionDefault: boolean;
  canSet: boolean;
  setting: boolean;
  onMakeDefault(): void;
}) {
  if (!linked) {
    return <>—</>;
  }

  if (isRegionDefault) {
    return <MutedText>Region default</MutedText>;
  }

  if (!canSet) {
    return <MutedText>Not region default</MutedText>;
  }

  return (
    <Button variant="secondary" size="sm" type="button" disabled={setting} onClick={onMakeDefault}>
      {setting ? 'Saving…' : 'Make default'}
    </Button>
  );
}

export const RegionFacilities: React.FC<RegionFacilitiesProps> = ({
  regionID,
  defaultWarehouse,
  defaultDepot,
  facilities,
  modifyingRegion,
  onUpdated,
}) => {
  const canUpdate = usePermissionKey('facility:update');
  const [modifyRegion] = useModifyRegionMutation({ client });
  const [pendingKey, setPendingKey] = React.useState<string | null>(null);
  const [showInactive, setShowInactive] = React.useState(false);

  const activeFacilities = facilities.filter((facility) => facility.active);
  const inactiveFacilities = facilities.filter((facility) => !facility.active);
  const visibleFacilities = showInactive ? facilities : activeFacilities;
  const inactiveCount = inactiveFacilities.length;

  const showStorageColumn = visibleFacilities.some((facility) => facility.warehouse?.id);
  const showDepotColumn = visibleFacilities.some((facility) => facility.depot?.id);

  const setDefault = async (input: { defaultWarehouseID?: string; defaultDepotID?: string }, pending: string) => {
    setPendingKey(pending);
    const result = await modifyRegion({
      variables: { id: regionID, input },
    });
    setPendingKey(null);

    if (!result.data?.modifyRegion?.error) {
      onUpdated();
    }
  };

  const columnCount = 4 + (showStorageColumn ? 1 : 0) + (showDepotColumn ? 1 : 0) + 1;

  return (
    <Card>
      <CardToolbar>
        <CardToolbarLead>
          <SectionTitle>Facilities</SectionTitle>
          <SectionMeta>
            {showInactive
              ? `${facilities.length} ${facilities.length === 1 ? 'facility' : 'facilities'}`
              : `${activeFacilities.length} active`}
            {!showInactive && inactiveCount > 0 && ` · ${inactiveCount} hidden`}
          </SectionMeta>
        </CardToolbarLead>
        <SectionActions>
          {inactiveCount > 0 && (
            <Button variant="secondary" size="sm" type="button" onClick={() => setShowInactive((value) => !value)}>
              {showInactive ? 'Hide inactive' : `Show ${inactiveCount} inactive`}
            </Button>
          )}
        </SectionActions>
      </CardToolbar>
      <MutedText style={{ display: 'block', padding: '12px 16px 0' }}>
        Linked facilities for this region. Use <strong>Make default</strong> to set Consumer Storage or Consumer Depot
        defaults.
      </MutedText>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Regions</th>
              <th>Services</th>
              {showStorageColumn && <th>Storage default</th>}
              {showDepotColumn && <th>Depot default</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleFacilities.length === 0 ? (
              <tr>
                <td colSpan={columnCount}>
                  <EmptyState>
                    {activeFacilities.length === 0 && inactiveCount > 0 && !showInactive
                      ? 'No active facilities for this region. Show inactive to view archived sites.'
                      : 'No facilities are associated with this region.'}
                  </EmptyState>
                </td>
              </tr>
            ) : (
              visibleFacilities.map((facility) => {
                const warehouseId = facility.warehouse?.id;
                const depotId = facility.depot?.id;
                const warehouseActive = facility.warehouse?.active ?? false;
                const depotActive = facility.depot?.active ?? false;
                const canSetStorageDefault = canUpdate && facility.active && warehouseActive;
                const canSetDepotDefault = canUpdate && facility.active && depotActive;
                const isDefaultWarehouse = warehouseId != null && String(defaultWarehouse?.id) === String(warehouseId);
                const isDefaultDepot = depotId != null && String(defaultDepot?.id) === String(depotId);

                return (
                  <tr key={facility.id}>
                    <td>
                      <ButtonLink to={facilityURL({ id: facility.id })}>{facility.name}</ButtonLink>
                    </td>
                    <td>
                      <Pill tone={facility.active ? 'success' : 'muted'}>
                        {facility.active ? 'Active' : 'Inactive'}
                      </Pill>
                    </td>
                    <td>{regionNames(facility)}</td>
                    <td>{serviceNames(facility) || '—'}</td>
                    {showStorageColumn && (
                      <td>
                        <RegionDefaultCell
                          linked={warehouseId != null}
                          isRegionDefault={isDefaultWarehouse}
                          canSet={canSetStorageDefault}
                          setting={modifyingRegion || pendingKey === `wh-${facility.id}`}
                          onMakeDefault={() => setDefault({ defaultWarehouseID: warehouseId! }, `wh-${facility.id}`)}
                        />
                      </td>
                    )}
                    {showDepotColumn && (
                      <td>
                        <RegionDefaultCell
                          linked={depotId != null}
                          isRegionDefault={isDefaultDepot}
                          canSet={canSetDepotDefault}
                          setting={modifyingRegion || pendingKey === `dp-${facility.id}`}
                          onMakeDefault={() => setDefault({ defaultDepotID: depotId! }, `dp-${facility.id}`)}
                        />
                      </td>
                    )}
                    <td style={{ textAlign: 'right' }}>
                      <ButtonLink variant="secondary" size="sm" to={facilityURL({ id: facility.id })}>
                        View details
                      </ButtonLink>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>
    </Card>
  );
};
